import type { RecommendModels } from '@algolia/recommend';
import type { ReactElement } from 'react';

import dynamic from 'next/dynamic';
import { useState, memo, useCallback } from 'react';

import { RecommendationsCarouselSkeleton } from './RecommendationsCarouselSkeleton';
import { FeatureFlag } from '../FeatureFlag';
import type { ProductRecommendations as RecommendationsSchema } from '~/amplienceTypes/schemas/imported/product-recommendations-schema';
import { useLazyLoading } from '~/utilities/hooks';

const YouMayAlsoLike = dynamic(() => import('./YouMayAlsoLike'), { ssr: false });

type YouMayAlsoLikeWrapperProps = RecommendationsSchema & {
  ariaLabel?: string;
  className?: string;
  model?: RecommendModels;
};

const YouMayAlsoLikeWrapperContent = (props: YouMayAlsoLikeWrapperProps): ReactElement | null => {
  const { ref, isIntersecting } = useLazyLoading<HTMLDivElement>({
    options: {
      root: null,
      rootMargin: '50% 10% 50% 10%',
      threshold: 0,
    },
  });
  const [isReady, setIsReady] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);

  const handleReady = useCallback((hasProducts: boolean) => {
    if (!hasProducts) {
      setIsEmpty(true);
    } else {
      setIsReady(true);
    }
  }, []);

  if (isEmpty) {
    return null;
  }

  return (
    <div ref={ref}>
      {!isReady && <RecommendationsCarouselSkeleton />}
      {isIntersecting && (
        <div style={!isReady ? { display: 'none' } : undefined}>
          <YouMayAlsoLike {...props} onReady={handleReady} />
        </div>
      )}
    </div>
  );
};

const YouMayAlsoLikeWrapperComponent = (props: YouMayAlsoLikeWrapperProps): ReactElement => (
  <FeatureFlag flag="enableYMALAlgoliaRecommendations">
    <YouMayAlsoLikeWrapperContent {...props} />
  </FeatureFlag>
);

export const YouMayAlsoLikeWrapper = memo(YouMayAlsoLikeWrapperComponent);
